import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";
import {
  ANDROID_MIN_OS,
  APP_STORE_URL,
  IOS_MIN_OS,
  PLAY_URL,
  StoreButtons,
} from "./StoreLinks";
import { isNativePlatform } from "../utils/platform";

// Public "get the app" page. Both platforms are live, so the copy stays platform-neutral and
// the store links are shown together rather than sniffing the visitor's device. Copy is adapted
// from playstore/listing.md rather than pasted: a page that is a verbatim copy of a store
// listing has no reason to be indexed alongside it.

const SCREENSHOTS = [
  {
    src: "/app/screenshot-request.webp",
    alt: "Choosing a vehicle year, make, model and trim in the GearCheckAuto app",
    caption: "Describe the vehicle and what it needs.",
  },
  {
    src: "/app/screenshot-shops.webp",
    alt: "A list of matched repair shops with match scores and Google ratings",
    caption: "See matched shops with ratings and specialties.",
  },
  {
    src: "/app/screenshot-quote.webp",
    alt: "A message thread where a repair shop has replied with a quote",
    caption: "Compare replies and quotes in one inbox.",
  },
];

// Kept verbatim from the Play listing - these are the stable claims, and rewording them here
// would let the two drift.
const FEATURES = [
  {
    title: "One request, many shops",
    body: "Stop calling around - reach several local shops at once.",
  },
  {
    title: "Compare with confidence",
    body: "See quotes and how quickly each shop responds, side by side.",
  },
  {
    title: "Mobile mechanics too",
    body: "Find mechanics who come to you, shown by their service area on the map.",
  },
  {
    title: "Message directly",
    body: "Chat with shops in the app to work out the details before you book.",
  },
  {
    title: "Pre-purchase inspections",
    body: "Buying a used car? Get it inspected by a nearby mechanic first.",
  },
  {
    title: "Free for vehicle owners",
    body: "Creating an account and sending requests is free - you only pay the shop you choose.",
  },
];

const FAQS = [
  {
    q: "Is the app free?",
    a: "Yes. Creating an account and sending requests is free for vehicle owners. You only pay the shop you choose to book with, and you are never obligated to book at all.",
  },
  {
    q: "Do I need the app to use GearCheckAuto?",
    a: "No. Everything works in a normal browser at gearcheckauto.com. The app mainly adds push notifications, so you hear about a shop's reply without keeping a tab open.",
  },
  {
    q: "Does my website account work in the app?",
    a: "It is the same account either way. Sign in with the email and password you already use, and your requests, messages, and favorite shops are all there.",
  },
  {
    q: "Why does the app ask for my location?",
    a: "Only to fill in your ZIP code when you tap Use my location, so nearby shops can be matched by distance. You can always type the ZIP yourself instead, and the app never tracks your location in the background.",
  },
  {
    q: "How do I delete my account?",
    a: "From your profile in the app or on the website. There is also a delete account page on this site if you would rather start there.",
  },
];

// One node per store. They are two distinct downloads with their own install URL and OS
// requirement, so modelling them as one entity would force a single installUrl and misstate
// whichever platform lost the coin toss.
function appNode({ id, storeUrl, operatingSystem, deviceWord }) {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "@id": `https://gearcheckauto.com/mobile-app#${id}`,
    name: "GearCheckAuto: Auto Repair",
    alternateName: "GearCheckAuto",
    url: "https://gearcheckauto.com/mobile-app",
    description:
      "Send one auto repair request to several nearby repair shops and mobile mechanics, compare " +
      `quotes and response times, and book the best fit - from your ${deviceWord}.`,
    applicationCategory: "UtilitiesApplication",
    applicationSubCategory: "Auto & Vehicles",
    operatingSystem,
    installUrl: storeUrl,
    downloadUrl: storeUrl,
    image: "https://gearcheckauto.com/logo-512.png",
    screenshot: SCREENSHOTS.map((shot) => `https://gearcheckauto.com${shot.src}`),
    // Resolves against the Organization node declared in public/index.html: Google merges every
    // JSON-LD block on the page into one graph before resolving @id, so this links the app to
    // the existing entity instead of declaring a duplicate publisher.
    publisher: { "@id": "https://gearcheckauto.com/#organization" },
    author: { "@id": "https://gearcheckauto.com/#organization" },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    // Deliberately no aggregateRating: the apps have no ratings yet, and publishing one that is
    // not shown on the page is a structured-data violation.
  };
}

const JSON_LD = [
  appNode({
    id: "android-app",
    storeUrl: PLAY_URL,
    operatingSystem: ANDROID_MIN_OS,
    deviceWord: "Android phone",
  }),
  appNode({
    id: "ios-app",
    storeUrl: APP_STORE_URL,
    operatingSystem: IOS_MIN_OS,
    deviceWord: "iPhone",
  }),
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      { "@type": "ListItem", position: 2, name: "Mobile app", item: "https://gearcheckauto.com/mobile-app" },
    ],
  },
];

export default function MobileAppPage({ onNavigate }) {
  // The same bundle runs inside the installed app, and a Capgo OTA can deliver this page there.
  // Telling someone to download an app they are already using is nonsense, so the store links
  // become a plain acknowledgement instead.
  const isNative = isNativePlatform();

  const storeCta = isNative ? (
    <p className="rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-4 py-3 text-sm font-medium text-slate-700 dark:text-gray-200">
      You are already using the GearCheckAuto app. Here is what it does.
    </p>
  ) : (
    <StoreButtons />
  );

  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white dark:to-gray-800 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Mobile app</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            Get the GearCheckAuto app
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            One request to nearby repair shops and mobile mechanics. Compare quotes and response
            times, message shops with questions, and book the best fit - from your phone.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            {storeCta}
            {!isNative && (
              <button
                type="button"
                onClick={() => onNavigate("signup")}
                className="rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              >
                Sign up on the web
              </button>
            )}
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-gray-400">
            Free for vehicle owners. {ANDROID_MIN_OS}, {IOS_MIN_OS}.
          </p>
        </div>
      </section>

      {/* What the app does */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            What you can do in the app
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {FEATURES.map((feature) => (
              <div key={feature.title}>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-gray-100">{feature.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-600 dark:text-gray-300">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots */}
      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            A look at the app
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3">
            {SCREENSHOTS.map((shot) => (
              <figure key={shot.src}>
                {/* Explicit width/height: public/ assets bypass any build-time image pipeline,
                    so these attributes are the only thing reserving layout space. */}
                <img
                  src={shot.src}
                  alt={shot.alt}
                  width={540}
                  height={960}
                  loading="lazy"
                  decoding="async"
                  className="w-full rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm"
                />
                <figcaption className="mt-3 text-sm text-slate-600 dark:text-gray-300">{shot.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Three steps */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">How it works</h2>
          <ol className="mt-8 space-y-6">
            {[
              "Tell us about your vehicle and the issue. Add photos or a short video so shops understand the problem.",
              "Send one request to several nearby repair shops and mobile mechanics.",
              "Compare quotes and response times, message shops with questions, and book the best fit.",
            ].map((step, index) => (
              <li key={step} className="flex gap-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-700 text-base font-bold text-white">
                  {index + 1}
                </span>
                <p className="leading-relaxed text-slate-600 dark:text-gray-300">{step}</p>
              </li>
            ))}
          </ol>
          <p className="mt-8 text-slate-600 dark:text-gray-300">
            There is a longer walkthrough, with a demo video, on the{" "}
            <SpaLink view="howItWorks" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
              How it works page
            </SpaLink>
            .
          </p>
        </div>
      </section>

      {/* Shops */}
      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            For repair shops and mobile mechanics
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600 dark:text-gray-300">
            Get matched with nearby customers who are ready to book. Receive requests, send quotes,
            and message customers in one place. Mobile mechanics can set a home base and service
            radius so nearby customers can find them.
          </p>
        </div>
      </section>

      {/* Requirements and permissions */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Requirements and permissions
          </h2>
          <dl className="mt-8 space-y-6 text-slate-600 dark:text-gray-300">
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">
                Android 6.0 or newer, or iOS 15.0 or newer
              </dt>
              <dd className="mt-1 leading-relaxed">
                The app is free to download on Google Play and the App Store, and free for vehicle
                owners to use.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">Location, only when you ask</dt>
              <dd className="mt-1 leading-relaxed">
                Tapping Use my location fills in your ZIP code so shops can be matched by distance.
                You can type the ZIP yourself instead, and the app does not track your location in
                the background.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">Notifications, optional</dt>
              <dd className="mt-1 leading-relaxed">
                Used to tell you when a shop replies to your request. Decline it and the app still
                works - you just check your inbox yourself.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-900 dark:text-gray-100">The same account as the website</dt>
              <dd className="mt-1 leading-relaxed">
                Sign in with the email and password you already use. See the{" "}
                <SpaLink view="privacy" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                  privacy policy
                </SpaLink>{" "}
                for what is collected, or the{" "}
                <SpaLink view="deleteAccount" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                  delete account page
                </SpaLink>{" "}
                if you want to remove it.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Browser */}
      <section className="bg-slate-50 dark:bg-gray-900 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
            Prefer your browser?
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600 dark:text-gray-300">
            You do not have to install anything. Everything on this page works at
            gearcheckauto.com in any browser, with the same account. You can add the site to your
            Home Screen to open it like an app - the installed app mainly adds push notifications,
            so you hear about a shop's reply without keeping a tab open.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Common questions</h2>
          <dl className="mt-8 space-y-8">
            {FAQS.map((item) => (
              <div key={item.q}>
                <dt className="text-lg font-semibold text-slate-900 dark:text-gray-100">{item.q}</dt>
                <dd className="mt-2 leading-relaxed text-slate-600 dark:text-gray-300">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">Get your next repair quote</h2>
          <p className="mt-3 leading-relaxed text-slate-300">
            Free for vehicle owners. No obligation, and real shops near you.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {storeCta}
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
            >
              Get started
            </button>
          </div>
          <p className="mt-8 text-xs text-slate-400">
            Google Play and the Google Play logo are trademarks of Google LLC. Apple, the Apple
            logo and App Store are trademarks of Apple Inc., registered in the U.S. and other
            countries.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
